/**
 * V2 Animation Preset select options (schema 2.17.0)
 *
 * Derived from the runtime PRESET_REGISTRY in @modules/screen-view-v2 so the
 * editor can only offer presets the renderer actually implements — a preset
 * added to the registry appears here automatically.
 */

// Deep import (not the package barrel) so the editor config doesn't pull the
// whole screen-view-v2 runtime (CSS + components, some using import.meta) into
// jsdom/jest — only the pure preset registry is needed here.
import { PRESET_REGISTRY, type PresetCategory } from '@modules/screen-view-v2/animations/presetRegistry';

export interface PresetOption {
  value: string;
  label: string;
}

/** camelCase preset name → human label ("fadeInUp" → "Fade In Up") */
function humanizePresetName(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function optionsForCategory(category: PresetCategory): PresetOption[] {
  return Object.entries(PRESET_REGISTRY)
    .filter(([, def]) => def.category === category)
    .map(([name]) => ({ value: name, label: humanizePresetName(name) }));
}

/** Entrance presets (screen entrance, item entrance, button enterAnimation) */
export const V2_ENTRANCE_PRESET_OPTIONS: PresetOption[] = optionsForCategory('entrance');

/** Emphasis presets (tapAnimation, selection feedback) */
export const V2_EMPHASIS_PRESET_OPTIONS: PresetOption[] = optionsForCategory('emphasis');

/** Continuous/loop presets */
export const V2_LOOP_PRESET_OPTIONS: PresetOption[] = optionsForCategory('loop');

/**
 * Idle-animation presets: emphasis + loop. `useTapTargetAnimation` forces
 * infinite iteration on the idle slot, so any emphasis preset (pulse,
 * heartbeat, …) loops correctly — restricting to the loop category alone would
 * hide the most common idle choices (e.g. a pulsing CTA).
 */
export const V2_IDLE_PRESET_OPTIONS: PresetOption[] = [
  ...V2_EMPHASIS_PRESET_OPTIONS,
  ...V2_LOOP_PRESET_OPTIONS,
];
